import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { Button, Typography, useMutationWithToast } from '@pesto/ui';
import { DetailRow } from '@pesto/ui/components/DetailRow';
import { useState } from 'react';
import { z } from 'zod';

import type { CreatePartnerMutation } from '../../../__generated__/graphql/api';
import type { ModalProps } from '../../types/modal.types';

import { ListPartnersDocument, useCreatePartnerMutation } from '../../../__generated__/graphql/api';
import { createFormComponent } from '../../utils/create-form-component';

const createPartnerSchema = z.object({
  name: z.string().min(1, 'Partner name is required'),
});

type CreatePartnerFormData = z.infer<typeof createPartnerSchema>;

const formConfig: SmartFormConfig<CreatePartnerFormData> = {
  fields: [
    {
      name: 'name',
      label: 'Partner Name',
      type: 'text',
      defaultValue: '',
      placeholder: 'e.g., Lafayette Credit Union',
      helperText:
        'Provisions the partner row, an Auth0 M2M client, and a partner_api identity. The partner starts inactive.',
    },
  ],
  schema: createPartnerSchema,
  defaultValues: {
    name: '',
  },
};

type ProvisioningResult = NonNullable<CreatePartnerMutation['createPartner']>;

export const CreatePartnerComponent = ({ api }: ModalProps<undefined, true>) => {
  const { handleMutation } = useMutationWithToast();
  const [provisioningResult, setProvisioningResult] = useState<ProvisioningResult | null>(null);

  const [createPartner, { loading }] = useCreatePartnerMutation({
    refetchQueries: [{ query: ListPartnersDocument }],
  });

  const handleSubmit = (data: CreatePartnerFormData) => {
    void handleMutation({
      mutation: () => createPartner({ variables: { input: { name: data.name.trim() } } }),
      extractSuccess: (result) => !!result?.data?.createPartner,
      debugLabel: 'create-partner',
      toast: {
        success: 'Partner provisioned',
        error: 'Failed to provision partner',
        loading: 'Provisioning partner...',
      },
      onSuccess: (result) => {
        const created = result?.data?.createPartner;

        if (created) setProvisioningResult(created);
      },
    });
  };

  if (provisioningResult) {
    return (
      <div className="flex flex-col gap-4">
        <Typography variant="body" className="text-destructive font-semibold">
          Copy the client secret now — it is never stored and cannot be shown again.
        </Typography>
        <DetailRow title="Partner" value={provisioningResult.partner.name} copyEnabled={false} />
        <DetailRow title="Partner ID" value={provisioningResult.partner.id} copyEnabled={true} />
        <DetailRow title="Client ID" value={provisioningResult.clientId} copyEnabled={true} />
        <DetailRow title="Client Secret" value={provisioningResult.clientSecret} copyEnabled={true} />
        <Button onClick={() => api.close(true)}>Done</Button>
      </div>
    );
  }

  return createFormComponent({
    config: formConfig,
    handleSubmit,
    loading,
  });
};
